import axios from "axios";
import { config } from "./config";
import { tokens } from "./tokens";
import { handleAxiosError } from "./utils/error.utils";
import { credentialsRequiredError, UnexpectedAPIError } from "./errors";
export const auth = (() => {
    // clean axios, no interceptors
    const _axios = axios.create();
    let interceptorInstancesMap = new Map();
    let _retryCounter = 0;
    function setConfig(data) {
        config.set(data);
    }
    function validCredentials(email, password) {
        if (!email || !password) {
            const errArguments = [];
            if (!email)
                errArguments.push("email");
            if (!password)
                errArguments.push("password");
            throw credentialsRequiredError(errArguments);
        }
    }
    async function login(email, password) {
        validCredentials(email, password);
        try {
            const url = `${config.fullUrl()}/tokens`;
            const data = { email, password };
            const response = await _axios.post(url, data);
            if (response.status === 200) {
                const storedTokens = tokens.set(response.headers);
                applyInterceptors();
                return storedTokens;
            }
            else {
                console.log("response.status NOT 200", response);
                throw response.data;
            }
        }
        catch (error) {
            if (error.response)
                handleAxiosError(error.response);
            else
                throw UnexpectedAPIError;
        }
    }
    function logout() {
        tokens.set();
    }
    async function refresh(error) {
        const { refreshToken } = tokens.get();
        if (!refreshToken) {
            console.error("RefreshWithoutRefreshTokenError");
            if (error)
                throw error;
            throw new Error("Unauthorized");
        }
        try {
            const url = `${config.fullUrl()}/tokens`;
            const requestConfig = {
                headers: { Authorization: `Bearer ${refreshToken}` },
            };
            const response = await _axios.get(url, requestConfig);
            if (response.status === 200) {
                const storedTokens = tokens.set(response.headers);
                return storedTokens;
            }
            else {
                throw response.data;
            }
        }
        catch (error) {
            _retryCounter = 0;
            if (error.response)
                handleAxiosError(error.response);
            else
                throw UnexpectedAPIError;
        }
    }
    function applyInterceptors(instance) {
        if (!interceptorInstancesMap.get(instance)) {
            instance = instance || axios; //normalize fallback to default instance.
            let ref = instance.interceptors.request.use(async (config) => {
                return await tokenInterceptor(config);
            });
            // Response interceptor
            instance.interceptors.response.use((value) => value, responseErrorHandler);
            interceptorInstancesMap.set(instance, ref);
        }
    }
    function ejectInterceptors(instance) {
        instance.interceptors.request.eject(interceptorInstancesMap.get(instance));
        interceptorInstancesMap.delete(instance);
    }
    async function tokenInterceptor(requestConfig) {
        const { token, initialisationToken } = tokens.get();
        const headers = { ...requestConfig.headers };
        if (token)
            headers.Authorization = `Bearer ${token}`;
        if (initialisationToken)
            headers.Initialisation = `Bearer ${initialisationToken}`;
        return { ...requestConfig, headers };
    }
    async function responseErrorHandler(error) {
        if (error &&
            error.response &&
            "status" in error.response &&
            error.response.status === 401 &&
            _retryCounter < 10) {
            try {
                _retryCounter++;
                await refresh(error);
                const { token } = tokens.get();
                const retryRequest = {
                    ...error.config,
                    headers: {
                        ...error.config.headers,
                        Authorization: `Bearer ${token}`,
                    },
                };
                return axios(retryRequest).then((response) => {
                    if (response && response.status > 199 && response.status < 300) {
                        _retryCounter = 0;
                    }
                    return response;
                });
            }
            catch (error) {
                _retryCounter = 0;
                if (error && error.response)
                    throw error.response;
                throw error;
            }
        }
        _retryCounter = 0;
        return Promise.reject(error);
    }
    const api = {
        config: setConfig,
        login: login,
        logout: logout,
        refresh: refresh,
        applyInterceptors: applyInterceptors,
        ejectInterceptors: ejectInterceptors,
        //   Getter is not defined yet, implementation is below. Otherwise the function is invoked immediately!
        isAuthenticated: (async () => {
            return { initialisationToken: "", token: "", refreshToken: "" };
        })(),
    };
    Object.defineProperty(api, "isAuthenticated", {
        get() {
            return (async () => {
                const { token, refreshToken } = tokens.get();
                if (token) {
                    applyInterceptors();
                    return { token: token, refreshToken: refreshToken };
                }
                if (refreshToken) {
                    try {
                        const response = await refresh();
                        applyInterceptors();
                        return response;
                    }
                    catch (error) {
                        //ignore error, this only serves as a boolean switch!
                        return false;
                    }
                }
                return false;
            })();
        },
    });
    return api;
})();
